/**
 * auoDecisions.ts — the AUO confirm-loop TRAINING LOG (Slice 5).
 *
 * Every confirm/reject appends ONE record to `clients/{id}/auo_decisions/{autoId}`
 * — the labelled dataset for improving the matrix/inference later. Cheap to write
 * (auto-id append), NEVER read by compose. A write failure must NEVER block the
 * decision itself (the fixture's status is already persisted on the floor) — the
 * caller fires this and ignores a rejection.
 *
 * Pure shape builder above the firebase import; the impure append below.
 */

/** One training-signal record (the persisted shape). */
export interface AuoDecisionRecord {
  /** the floor the AUO is on. */
  floor_id: string;
  /** the AUO name as marked (original, pre-normalise). */
  name: string;
  /** the 16-zone code it was marked in. */
  zone: string;
  /** the engine's disposition at decision time ('NO'|'TOL'|'OK'|'-'|null). */
  engine_disposition: string | null;
  /** the engine's severity at decision time (0..3). */
  engine_severity: number;
  /** the staff action. */
  staff_action: 'confirm' | 'reject';
  /** who decided. */
  staff_uid: string | null;
}

/** PURE — assemble the decision record from a mark-like input. Defensive so a
 *  partial mark never throws (this is fire-and-forget telemetry). */
export function buildDecisionRecord(input: {
  floorId: string;
  name: string;
  zone: string;
  disposition?: string | null;
  severity?: number;
  action: 'confirm' | 'reject';
  staffUid: string | null;
}): AuoDecisionRecord {
  return {
    floor_id: input.floorId || '',
    name: (input.name || '').trim(),
    zone: (input.zone || '').trim().toUpperCase(),
    engine_disposition: input.disposition === undefined ? null : input.disposition,
    engine_severity:
      typeof input.severity === 'number' && Number.isFinite(input.severity)
        ? input.severity
        : 0,
    staff_action: input.action,
    staff_uid: input.staffUid || null,
  };
}

/* ─────────────────────────── impure (Firestore append) ─────────────────────────── */

/**
 * Append one decision to `clients/{clientId}/auo_decisions/{autoId}` (+ a server
 * `at` timestamp). Auto-id, append-only. Best-effort — the caller does not await
 * the result for the UI (the decision already persisted on the floor). Returns
 * the new doc id on success.
 */
export async function logAuoDecision(
  clientId: string,
  record: AuoDecisionRecord,
): Promise<string | null> {
  if (!clientId) return null;
  try {
    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const ref = await addDoc(collection(db, 'clients', clientId, 'auo_decisions'), {
      ...record,
      at: serverTimestamp(),
    });
    return ref.id;
  } catch {
    // telemetry only — never surface / block the decision.
    return null;
  }
}
